import React, { useState, useEffect } from "react";
import { auth, db, ref, onValue, get } from "./firebase";
import { useAuth } from "./AuthContext";
import { FiAward, FiCalendar, FiFileText } from "react-icons/fi";

const Exams = () => {
  const [exams, setExams] = useState([]);
  const [username, setUsername] = useState("Loading...");
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { enrolledCourse } = useAuth();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          setError("User not authenticated. Please log in.");
          setLoading(false);
          return;
        }

        const email = currentUser.email;
        let foundUser = null;
        let userCourse = null;

        // First check if user is admin/assistant in users collection
        const usersRef = ref(db, "users");
        const usersSnapshot = await get(usersRef);

        if (usersSnapshot.exists()) {
          const usersData = usersSnapshot.val();
          foundUser = Object.values(usersData).find(
            (u) => u.email === email
          );
          if (foundUser) {
            setUsername(foundUser.name);
            setUserId(foundUser.studentId);
            userCourse = foundUser.enrolledCourse;
          }
        }

        // If not found in users collection, search in courses/students
        if (!foundUser || !userCourse) {
          const coursesRef = ref(db, "courses");
          const coursesSnapshot = await get(coursesRef);

          if (coursesSnapshot.exists()) {
            const courses = coursesSnapshot.val();

            // Search through each course's students
            for (const [courseId, courseData] of Object.entries(courses)) {
              if (courseData.students) {
                const student = Object.values(courseData.students).find(
                  (s) => s.email === email
                );
                if (student) {
                  foundUser = student;
                  setUsername(student.name);
                  setUserId(student.studentId);
                  userCourse = courseId;
                  break;
                }
              }
            }
          }
        }

        if (!foundUser) {
          setError("User data not found. Please complete your profile.");
          setLoading(false);
          return;
        }

        if (!userCourse) {
          setError("No enrolled course found. Please enroll in a course.");
          setLoading(false);
          return;
        }

        // Fetch all exams from the course
        const courseRef = ref(db, `courses/${userCourse}`);
        const courseSnapshot = await get(courseRef);

        if (courseSnapshot.exists()) {
          const courseData = courseSnapshot.val();
          const courseExams = courseData.exams || {};

          // Get student's exam grades
          const studentExamGrades = foundUser.examGrades || {};

          // Format all exams, marking unattended ones
          const formattedExams = Object.entries(courseExams)
            .map(([examId, examDetails]) => {
              const studentGrade = studentExamGrades[examId];
              return {
                id: examId,
                name: examDetails.name || examId,
                score: studentGrade?.mark || "Not Attended",
                total: examDetails.totalMark || 100,
                grade: studentGrade?.grade || "N/A",
                comment: studentGrade?.comment || "Not attended",
                published: examDetails.published !== false, // Default to true
                date: examDetails.date || "Not specified"
              };
            })
            .filter(exam => exam.published) // Only show published exams
            .sort((a, b) => {
              // Sort by date if available, otherwise by name
              if (a.date && b.date) {
                return new Date(b.date) - new Date(a.date);
              }
              return a.name.localeCompare(b.name);
            });

          setExams(formattedExams);
        } else {
          setExams([]);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching user data:", error);
        setError("Failed to fetch user data. Please try again later.");
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const getGradeColor = (grade) => {
    if (grade === "N/A") return "bg-gray-500/20 text-gray-400";
    switch (grade) {
      case "A*": return "bg-green-600/20 text-green-400";
      case "A": return "bg-green-500/20 text-green-400";
      case "B": return "bg-teal-500/20 text-teal-400";
      case "C": return "bg-yellow-500/20 text-yellow-400";
      case "D": return "bg-orange-500/20 text-orange-400";
      default: return "bg-red-500/20 text-red-400";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="bg-red-500/10 p-6 rounded-xl w-full max-w-md text-center border border-red-500/20">
          <div className="text-red-400 font-medium text-lg mb-2">Error</div>
          <p className="text-gray-300">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 py-6 w-full">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-teal-600 to-sky-600 rounded-2xl p-6 md:p-8 shadow-xl mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Exams</h1>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-teal-100 text-sm md:text-base">Total Exams:</span>
            <span className="text-white font-bold text-lg md:text-xl">{exams.length}</span>
          </div>
        </div>
      </div>

      {/* Exams List */}
      <div className="grid grid-cols-1 gap-4 md:gap-6">
        {exams.length > 0 ? (
          exams.map((exam) => (
            <div
              key={exam.id}
              className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 overflow-hidden hover:border-teal-500/30 transition-all duration-300"
            >
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-xl bg-teal-500/20 flex items-center justify-center text-teal-400">
                      <FiFileText className="text-xl" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">{exam.name}</h3>
                      <div className="flex items-center space-x-3 text-sm text-gray-400">
                        <div className="flex items-center space-x-1">
                          <FiCalendar className="text-gray-400" />
                          <span>{exam.date}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <FiAward className="text-gray-400" />
                          <span>{exam.score}/{exam.total}</span>
                        </div>
                        <div className={`flex items-center space-x-1 px-3 py-1 rounded-full ${getGradeColor(exam.grade)}`}>
                          <span>{exam.grade}</span>
                        </div>
                      </div>
                      {exam.comment && (
                        <p className="mt-3 text-sm text-gray-400">
                          <span className="text-gray-500">Comment: </span>
                          {exam.comment}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-8 text-center">
            <FiFileText className="mx-auto text-4xl text-gray-400 mb-4" />
            <p className="text-gray-400 text-lg">No exam records found</p>
            <p className="text-gray-500 mt-2">Please check back later for updates</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Exams;